import { useMemo } from 'react';

import type { StoreItem, StoreItemsByCategory } from '../types/get-store-items';
import { isOnPromotion } from '../utils/promotion';

/**
 * Deriva a lista de produtos em promoção a partir do cardápio já carregado
 * (`items` do endpoint `merchants/{slug}/items`), achatando as categorias em
 * uma única lista.
 *
 * Um produto entra na lista apenas quando tem uma promoção **válida** segundo
 * `utils/promotion.ts` (preço de venda finito, positivo e estritamente menor
 * que o preço cheio) — assim descontos ausentes, zerados ou inválidos nunca são
 * exibidos como promoção (default seguro).
 *
 * A saída é desduplicada por `id`: como as categorias virtuais do backend (ex.:
 * "Promoções") podem repetir o mesmo produto que já aparece na sua categoria
 * real, o mesmo item não é listado duas vezes. A ordem de primeira ocorrência é
 * preservada. O resultado é memoizado e só recalcula quando o cardápio muda.
 */
export function usePromotionalItems(
  itemsByCategory: StoreItemsByCategory | undefined | null
): StoreItem[] {
  return useMemo(() => {
    if (!itemsByCategory) return [];

    const promotional: StoreItem[] = [];
    const seen = new Set<string>();

    for (const categoryItems of Object.values(itemsByCategory)) {
      if (!Array.isArray(categoryItems)) continue;

      for (const item of categoryItems) {
        // Ignora entradas malformadas e itens já vistos (mesmo `id`) antes de
        // avaliar a promoção, evitando duplicatas vindas de categorias virtuais.
        if (!item || typeof item.id !== 'string' || seen.has(item.id)) continue;

        if (isOnPromotion(item)) {
          seen.add(item.id);
          promotional.push(item);
        }
      }
    }

    return promotional;
  }, [itemsByCategory]);
}
